import { usePostHog } from '@posthog/react';
import { useCallback } from 'react';
import type { PosthogEventProperties } from '@/utils/posthogEventProperties/posthogEventProperties';
import { PosthogEvent } from '@/utils/posthogEvents';

/**
 * Permissive property shape carried by **legacy** PostHog events — those that
 * haven't yet been migrated into {@link PosthogEventProperties}. PostHog itself
 * accepts any JSON-serialisable payload; this shape just narrows what we allow
 * call sites to pass.
 *
 * New events should be added to a domain module under
 * `apps/client/src/utils/posthogEventProperties/` so the strict path of
 * `trackEvent` enforces their schema at compile time.
 */
type LegacyTrackEventProperties = Record<
  string,
  string | number | boolean | Array<number | string | boolean | { [key: string]: unknown }> | null
>;

/**
 * Resolves the property-argument tuple for a given event:
 *   - if the event has a typed entry in {@link PosthogEventProperties}, the
 *     properties argument is **required** and must match that exact shape;
 *   - otherwise (legacy event) the argument is **optional** and accepts the
 *     permissive `Record<string, …>` shape so the ~116 untouched call sites
 *     keep compiling without retrofit.
 *
 * Encoded as a rest-parameter tuple so a single generic signature can express
 * both shapes. Conditional types are used instead of function overloads
 * because overload resolution falls through to a later signature when the
 * first fails to match, which would let strictly-typed events silently bypass
 * their property contract.
 */
type TrackEventArgs<E extends PosthogEvent> = E extends keyof PosthogEventProperties
  ? [properties: PosthogEventProperties[E]]
  : [properties?: LegacyTrackEventProperties];

/**
 * The fully-resolved signature of `trackEvent` returned by the hook. Extracted
 * so the type can be passed around (e.g. by `TrackEventType` below) without
 * recapitulating the generic.
 */
type TrackEventFn = <E extends PosthogEvent>(event: E, ...args: TrackEventArgs<E>) => void;

const usePosthogTracking = () => {
  const posthog = usePostHog();

  /**
   * Track an event with PostHog.
   *
   * Type contract picks one of two overloads based on whether the event has a
   * typed property bag in `PosthogEventProperties`:
   *   - **Strict**: required, exactly-typed properties (e.g. `PORTFOLIO_CREATED`)
   *   - **Permissive**: legacy record shape for events that haven't been
   *     migrated yet
   *
   * @param event the event name, following our event naming convention
   * @param properties properties to attach to the event (shape depends on
   *   whether the event is in `PosthogEventProperties`)
   */
  const trackEvent: TrackEventFn = useCallback(
    (event: PosthogEvent, properties?: LegacyTrackEventProperties) => {
      try {
        posthog?.capture(event, properties);
      } catch (e) {
        console.error(e);
      }
    },
    [posthog],
  );

  return {
    trackEvent,
  };
};

export type TrackEventType = ReturnType<typeof usePosthogTracking>['trackEvent'];

export default usePosthogTracking;
